import { fetch } from 'domain-task';

import { BaseApplicationState } from '@common/react/store';
import { BaseUser } from '@common/react/objects/BaseUser';
import { BaseParams } from '@common/typescript/objects/BaseParams';

interface Message<T> {
	success: number;
	response: T;
	session: string;
}

export interface ResponseError {
	message: string;
	code: number;
	name?: string;
}

interface baseRequestOptions {
	transformBody?: (type: string, data?: BaseParams) => string;
	url?: string;
}

const baseRequest = <
	T,
	TUser extends BaseUser,
	TApplicationState extends BaseApplicationState<TUser>
>(type: string, data: BaseParams = {}, state?: TApplicationState, signal?: AbortSignal, options?: baseRequestOptions): Promise<T> => {
	const {
		transformBody = (type, params) => JSON.stringify({
			type,
			data: JSON.stringify(params),
		}),
		url = 'api/post',
	} = options || {};
	return fetch(url, {
		credentials: 'same-origin',
		method: 'POST',
		headers: {
			'Content-type': 'application/json; charset=utf-8',
			Cookie: `session=${state ? state.login.session : ''}`,
		},
		body: transformBody(type, data),
		signal,
	})
		.then((response) => response.json() as Message<T | ResponseError>)
		.then((data: Message<T | ResponseError>) => {
			if (!data.success) {
				throw data.response as ResponseError;
			}

			return data.response as T;
		});
};

const request = <
	T,
	TUser extends BaseUser,
	TApplicationState extends BaseApplicationState<TUser>
	>(type: string, data: BaseParams = {}, state?: TApplicationState, signal?: AbortSignal, options?: baseRequestOptions): Promise<T> => {
	return baseRequest<T, TUser, TApplicationState>(type, data, state, signal)
		.catch((error: ResponseError) => {
			if (error.name === 'AbortError') {
				throw new Error('Aborted');
			}
			if (error.message === 'Access denied' && window) {
				window.location.href = '/';
			}

			console.log(error.message);
			throw error.message as string;
		});
};

export { baseRequest, request };
